/* Audit Rating gauge — the panel's own SVG of the same 5-band scale the PDF
   prints (backend charts.build_rating_gauge_svg). The backend renders that gauge
   as an image for WeasyPrint; sending image bytes to the browser would be wasteful
   and wouldn't respect the theme, so the panel draws it from the same two numbers
   (label + gauge_position 0..1). */
import type { DraftGenerated } from '../../services/api';

/* Left (worst) → right (best), matching gauge_position 0..1. */
const BANDS = [
  { label: 'Unacceptable risk exposure', colour: 'var(--fail)' },
  { label: 'Major Improvement Opportunity', colour: 'var(--accent)' },
  { label: 'Some Improvement Opportunity', colour: 'var(--watch)' },
  { label: 'Satisfactory', colour: '#7CB342' },
  { label: 'Well-Managed', colour: 'var(--pass)' },
];

export function AuditRatingGauge({
  rating,
}: {
  rating: DraftGenerated['audit_rating'];
}): JSX.Element {
  const activeIndex = BANDS.findIndex((b) => b.label === rating.label);
  // gauge_position is authoritative; the band index is only a fallback for a
  // label the backend vocabulary doesn't know about.
  const pos = Math.min(
    1,
    Math.max(
      0,
      typeof rating.gauge_position === 'number'
        ? rating.gauge_position
        : (activeIndex + 0.5) / BANDS.length,
    ),
  );

  return (
    <div className="dr-gauge">
      <div
        className="dr-gauge-track"
        role="img"
        aria-label={`Audit rating: ${rating.label}`}
      >
        {BANDS.map((b, i) => (
          <span
            key={b.label}
            className={`dr-gauge-band${i === activeIndex ? ' active' : ''}`}
            style={{ background: b.colour }}
          />
        ))}
        <span className="dr-gauge-needle" style={{ left: `${pos * 100}%` }} />
      </div>

      <div className="dr-gauge-scale" aria-hidden="true">
        {BANDS.map((b, i) => (
          <span
            key={b.label}
            className={`dr-gauge-tick${i === activeIndex ? ' active' : ''}`}
          >
            {b.label}
          </span>
        ))}
      </div>

      <p className="dr-gauge-label">
        <strong>{rating.label}</strong>
        {rating.subtitle ? <span className="t-muted"> — {rating.subtitle}</span> : null}
      </p>
    </div>
  );
}
